const COMPANY_ID = "M8Pf2t1qfoYY";
const API_ORIGIN = "https://api.logfrog.io";
const IDN_KEY = `logfrog.idn.${COMPANY_ID}`;
const VISITOR_KEY = `logfrog.vid.${COMPANY_ID}`;
const ANALYTICS_SESSION_KEY = `logfrog.analytics.sid.${COMPANY_ID}`;
const ANALYTICS_LAST_KEY = `logfrog.analytics.lst.${COMPANY_ID}`;
const ANALYTICS_SESSION_GAP_MS = 30 * 60 * 1000;
// Acquisition and billing funnels need exact consenting-user counts. Sampling
// here made nine out of ten visits and payment-attribution joins invisible,
// while identify still ran for every account.
const ANALYTICS_SAMPLE_PERCENT = 100;
const ANALYTICS_SAMPLE_SALT = "dealhawk-logfrog-v2:";

type LogfrogIdentity = { user_id: string; name?: string };

type PendingCall =
  | { type: "identify"; identity: LogfrogIdentity }
  | { type: "reset" }
  | { type: "openChat" }
  | { type: "pageview" };

const pending: PendingCall[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let activeIdentity: LogfrogIdentity | null = null;
let coverageIdentity = "";

function randomId(prefix: string) {
  return prefix + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function isWidgetReady() {
  return window.__logfrogLoaded === true;
}

function isLogfrogDisabled() {
  return window.__logfrogDisabled === true;
}

function respectsDoNotTrack() {
  try {
    const nav = navigator as Navigator & { msDoNotTrack?: string };
    const dnt = nav.doNotTrack || nav.msDoNotTrack;
    return dnt === "1" || dnt === "yes";
  } catch {
    return false;
  }
}

/** Stable user-level sampling so accounts do not move in and out between sessions. */
export function isLogfrogAnalyticsSampled(key: string): boolean {
  const normalized = `${ANALYTICS_SAMPLE_SALT}${String(key).trim().toLowerCase()}`;
  if (normalized === ANALYTICS_SAMPLE_SALT) return false;

  // FNV-1a provides a small, deterministic distribution without persisting a
  // separate sampling decision or exposing account identifiers.
  let hash = 0x811c9dc5;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 100 < ANALYTICS_SAMPLE_PERCENT;
}

function readStoredVisitorId(storage: () => Storage): string | null {
  try {
    // Touching window.localStorage itself throws when site data is blocked.
    const value = storage().getItem(VISITOR_KEY);
    return value && value.length >= 8 ? value : null;
  } catch {
    return null;
  }
}

// One visitor id per browser (not per tab session) so a landing-page visit can
// be joined to an install or signup that happens days later. Older builds kept
// it in sessionStorage; adopt that value once so an in-flight visit keeps its id.
let memoryVisitorId: string | null = null;

function getOrCreateVisitorId() {
  const persisted = readStoredVisitorId(() => localStorage);
  if (persisted) return persisted;
  const legacy = readStoredVisitorId(() => sessionStorage);
  const id = legacy || memoryVisitorId || randomId("v");
  try {
    localStorage.setItem(VISITOR_KEY, id);
  } catch {
    try {
      sessionStorage.setItem(VISITOR_KEY, id);
    } catch {
      /* Storage blocked: keep the id for this page load only. */
    }
  }
  memoryVisitorId = id;
  return id;
}

/** LogFrog web visitor id (created if missing); used to join web visits to app installs. */
export function getLogfrogVisitorId(): string {
  return getOrCreateVisitorId();
}

function isCurrentAnalyticsSampled(identity: LogfrogIdentity | null = activeIdentity) {
  return isLogfrogAnalyticsSampled(identity?.user_id || getOrCreateVisitorId());
}

function getOrCreateAnalyticsSession() {
  const now = Date.now();
  try {
    const last = Number(sessionStorage.getItem(ANALYTICS_LAST_KEY) || "0");
    let sessionId = sessionStorage.getItem(ANALYTICS_SESSION_KEY) || "";
    let sessionNew = false;
    if (!sessionId || !last || now - last > ANALYTICS_SESSION_GAP_MS) {
      sessionId = randomId("s_");
      sessionNew = true;
      sessionStorage.setItem(ANALYTICS_SESSION_KEY, sessionId);
    }
    sessionStorage.setItem(ANALYTICS_LAST_KEY, String(now));
    return { sessionId, sessionNew };
  } catch {
    return { sessionId: randomId("s_"), sessionNew: true };
  }
}

function normalizePathname(pathname: string) {
  const value = String(pathname || "/").trim();
  return (value.startsWith("/") ? value : "/").slice(0, 1024) || "/";
}

function normalizeSearch(search: string) {
  const value = String(search || "");
  if (!value) return "";
  return (value.startsWith("?") ? value : `?${value}`).slice(0, 1024);
}

export function buildLogfrogPageviewLocation(input: {
  origin: string;
  pathname: string;
  search?: string;
  isAuthenticated: boolean;
}) {
  const pathname = normalizePathname(input.pathname);
  // Anonymous URLs are deliberately path-only. Search terms, ZIP codes,
  // auth callback tokens, and other user-entered query values stay local.
  const search = input.isAuthenticated ? normalizeSearch(input.search || "") : "";
  const origin = String(input.origin || "").replace(/\/$/, "");
  return { pathname, href: `${origin}${pathname}${search}`.slice(0, 2048) };
}

function postAnalytics(payload: Record<string, unknown>, identity: LogfrogIdentity | null = null) {
  if (isLogfrogDisabled() || respectsDoNotTrack()) return;
  if (!isCurrentAnalyticsSampled(identity)) return;
  const session = getOrCreateAnalyticsSession();
  fetch(`${API_ORIGIN}/api/widget/${encodeURIComponent(COMPANY_ID)}/analytics/collect`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "omit",
    mode: "cors",
    keepalive: true,
    body: JSON.stringify({
      ...payload,
      sessionId: session.sessionId,
      sessionNew: session.sessionNew || undefined,
      visitorId: getOrCreateVisitorId(),
      ...(identity ? { user_id: identity.user_id, anonymous: false } : { anonymous: true }),
    }),
  }).then((response) => {
    if (!response.ok) console.warn("Logfrog delivery rejected", { httpStatus: response.status });
  }).catch(() => { console.warn("Logfrog delivery unavailable"); });
}

function setSignedInAnalyticsEnabled(enabled: boolean) {
  window.logfrog?.init?.({
    features: {
      analytics: { active: enabled && isCurrentAnalyticsSampled(), respectDnt: true },
    },
  });
}

function persistIdentity(identity: LogfrogIdentity) {
  try {
    localStorage.setItem(IDN_KEY, JSON.stringify(identity));
  } catch {
    try {
      sessionStorage.setItem(IDN_KEY, JSON.stringify(identity));
    } catch {
      /* ignore */
    }
  }
}

function syncIdentifyToServer(identity: LogfrogIdentity) {
  if (isLogfrogDisabled() || respectsDoNotTrack()) return;
  fetch(`${API_ORIGIN}/api/widget/${encodeURIComponent(COMPANY_ID)}/identify`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "omit",
    mode: "cors",
    body: JSON.stringify({
      visitorId: getOrCreateVisitorId(),
      user_id: identity.user_id,
      name: identity.name,
      anonymous: false,
    }),
  }).catch(() => {});
}

function runCall(call: PendingCall) {
  switch (call.type) {
    case "identify":
      window.logfrog?.identify?.(call.identity);
      return true;
    case "reset":
      window.logfrog?.reset?.();
      return true;
    case "openChat":
      window.logfrog?.openChat?.();
      return true;
    case "pageview":
      window.dispatchEvent(new PopStateEvent("popstate"));
      return true;
  }
}

function flushPending() {
  if (!isWidgetReady()) return;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  const batch = pending.splice(0);
  for (const call of batch) runCall(call);
}

function enqueue(call: PendingCall) {
  if (isLogfrogDisabled()) return;
  if (isWidgetReady() && runCall(call)) return;
  pending.push(call);
  if (flushTimer) return;
  flushTimer = setInterval(flushPending, 50);
}

export function identifyLogfrogUser(user: { userId: string; name?: string }) {
  const user_id = String(user.userId).trim();
  if (!user_id) return;
  const identity: LogfrogIdentity = { user_id, ...(user.name ? { name: user.name } : {}) };
  activeIdentity = identity;
  setSignedInAnalyticsEnabled(true);
  persistIdentity(identity);
  syncIdentifyToServer(identity);
  if (isCurrentAnalyticsSampled(identity)) {
    const coverageKey = `${user_id}:${getOrCreateAnalyticsSession().sessionId}`;
    if (coverageIdentity !== coverageKey) {
      coverageIdentity = coverageKey;
      trackLogfrogEvent("conversion_tracking_ready", { schema_version: 1, platform: "web" });
    }
  } else {
    coverageIdentity = "";
  }
  enqueue({ type: "identify", identity });
}

export function resetLogfrog(options: { rotateVisitor?: boolean } = {}) {
  activeIdentity = null;
  coverageIdentity = "";
  // Do not replay a previous account's queued identity after logout.
  pending.length = 0;
  setSignedInAnalyticsEnabled(false);
  try {
    localStorage.removeItem(IDN_KEY);
    // The anonymous app shell resets on every signed-out load; only an
    // explicit logout starts a new visitor so the next account is not joined
    // to the previous one.
    if (options.rotateVisitor) {
      memoryVisitorId = null;
      localStorage.removeItem(VISITOR_KEY);
      sessionStorage.removeItem(VISITOR_KEY);
    }
    sessionStorage.removeItem(IDN_KEY);
    sessionStorage.removeItem(ANALYTICS_SESSION_KEY);
    sessionStorage.removeItem(ANALYTICS_LAST_KEY);
  } catch {
    /* ignore */
  }
  enqueue({ type: "reset" });
}

export function openLogfrogChat() {
  enqueue({ type: "openChat" });
}

/** Explicit SPA pageview for React Router navigations. */
export function trackLogfrogPageview(input: {
  pathname: string;
  search?: string;
  isAuthenticated: boolean;
}) {
  if (input.isAuthenticated) {
    setSignedInAnalyticsEnabled(true);
    if (activeIdentity && isCurrentAnalyticsSampled()) enqueue({ type: "pageview" });
    return;
  }

  setSignedInAnalyticsEnabled(false);
  const location = buildLogfrogPageviewLocation({
    origin: window.location.origin,
    pathname: input.pathname,
    search: input.search,
    isAuthenticated: false,
  });
  postAnalytics({
    type: "pageview",
    pathname: location.pathname,
    hostname: window.location.hostname,
    href: location.href,
    title: document.title.slice(0, 300),
    screen: window.screen?.width
      ? `${window.screen.width}x${window.screen.height}`
      : undefined,
    language: navigator.language || undefined,
  });
}

/** Send immediately with keepalive so checkout navigation/widget loading cannot lose events. */
export function trackLogfrogEvent(name: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined" || !name.trim()) return;
  const eventName = name.trim().slice(0, 120);
  postAnalytics({
    type: "event",
    eventName,
    props: properties,
    pathname: normalizePathname(window.location.pathname),
    title: document.title.slice(0, 300),
  }, activeIdentity);
}

/** Metadata Logfrog uses to join a Stripe payment to this visitor/session. */
export function getLogfrogPaymentMetadata(): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (isLogfrogDisabled() || respectsDoNotTrack()) return {};
  if (!isCurrentAnalyticsSampled()) return {};
  const metadata = window.logfrog?.stripeMetadata?.();
  if (!metadata || typeof metadata !== "object") return {
    logfrog_company_id: COMPANY_ID,
    logfrog_visitor_id: getOrCreateVisitorId(),
    logfrog_session_id: getOrCreateAnalyticsSession().sessionId,
  };

  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([key, value]) => key.startsWith("logfrog_") && typeof value === "string" && value.length > 0
    )
  );
}

export function addLogfrogPaymentMetadata(params: URLSearchParams): URLSearchParams {
  for (const [key, value] of Object.entries(getLogfrogPaymentMetadata())) {
    params.set(key, value);
  }
  return params;
}
